import {Injectable} from '@angular/core';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls';
import {CameraService} from '../Scene/camera.service';
import {SceneService} from '../Scene/scene.service';
import {RenderService} from '../Scene/render.service';
import {Mesh, MOUSE, Raycaster, TOUCH, Vector2, Vector3} from 'three';
import {DragControls} from 'three/examples/jsm/controls/DragControls';
import {ContainerService} from '../../Utils/container.service';

@Injectable({
    providedIn: 'root'
})
export class OrbitControlsService {

    public orbitControls: OrbitControls;
    public dragControls: DragControls;
    public transform: any;

    // get
    private _dragEnabled = false;

    private _minDist = 5;
    private _maxDist = 100;

    constructor(private cameraService: CameraService,
                private sceneService: SceneService,
                private renderService: RenderService,
                private containerService: ContainerService) {


    }

    get dragEnabled(): boolean {
        return this._dragEnabled;
    }

    // TODO: change data type
    /**
     *
     * @param value: true: standard controls: rotate with left mouse button / one finger
     *               false: alternative controls: rotate with right mouse button / two fingers
     */
    set controlMode(value: boolean) {

        if (this.orbitControls) {
            if (value) {
                this.orbitControls.mouseButtons = {LEFT: MOUSE.ROTATE, MIDDLE: MOUSE.DOLLY, RIGHT: MOUSE.PAN};
                this.orbitControls.touches = {ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_PAN};
            } else {
                this.orbitControls.mouseButtons = {LEFT: MOUSE.PAN, MIDDLE: MOUSE.DOLLY, RIGHT: MOUSE.ROTATE};
                this.orbitControls.touches = {ONE: TOUCH.PAN, TWO: TOUCH.DOLLY_ROTATE};
            }
        }

    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    /**
     * initializes new orbit controls as model controls
     * initializes new drag controls as image controls
     */
    public initControls(renderer) {
        this.initOrbitControls(renderer);
        this.initDragControls(renderer);
    }

    /**
     * (de-)activates orbit controls and image dragging
     * @param val: should controls be enabled?
     */
    public changeControlMode(val: Boolean) {
        if (this.orbitControls) {
            if (val) {
                this.orbitControls.enabled = true;
                if (this._dragEnabled) {
                    this.activateDrag(true, false);
                }
            } else {
                this.orbitControls.enabled = false;
                this.activateDrag(false, false);
            }
        }
    }

    /**
     * resets controls to standard values
     */
    public ResetControl() {
        this.containerService.helpPlane.children[0].position.set(0, 0, 0);
        this.containerService.imagePlane.children[0].position.set(0, 0, 0);

        this.orbitControls.minDistance = this._minDist;
        this.orbitControls.maxDistance = this._maxDist;
        this.orbitControls.reset();
        this.sceneService.getGroupByName('lightGroup').children[1].position.copy(new Vector3(0, 0, 0));

    }

    /**
     * sets camera view for specific position and rotation center
     * @param camera: new camera position
     * @param target: new target for orbit controls == rotation center
     */
    public setQuickview(camera: Vector3, target: Vector3) {
        this.orbitControls.reset();
        this.orbitControls.target = target;
        this.cameraService.camera.position.set(camera.x, camera.y, camera.z);
        this.renderService.render();
    }

    /**
     * (de-)activates drag controls
     * @param value: activate dragging?
     */
    public activateDrag(value: boolean, changeMod: boolean = true) {
        if (changeMod) {
            this._dragEnabled = value;
        }
        if (value) {
            this.dragControls.activate();
        } else {
            this.dragControls.deactivate();
        }
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    private OrbitChange() {
        this.sceneService.getGroupByName('lightGroup').children[0].position.copy(this.cameraService.camera.position);
        this.renderService.render();
    }

    private initDragControls(renderer) {
        // remove all event listeners from old drag controls
        if (this.dragControls) {
            this.dragControls.dispose();
        }

        this.dragControls = new DragControls(this.containerService.imagePlane.children, this.cameraService.camera, renderer.domElement);
        this.activateDrag(false);

        this.dragControls.addEventListener('drag', (event) => {
            this.renderService.render();
        });
        this.dragControls.addEventListener('dragstart', (event) => {
            this.orbitControls.enabled = false;
        });
        this.dragControls.addEventListener('dragend', (event) => {
            this.orbitControls.enabled = true;
            this.orbitControls.update();
        });
    }

    private initOrbitControls(renderer) {
        // remove all event listeners from old orbit controls
        if (this.orbitControls) {
            this.orbitControls.dispose();
        }

        this.orbitControls = new OrbitControls(this.cameraService.camera, renderer.domElement);

        this.orbitControls.enabled = true;
        this.orbitControls.enableZoom = true;
        this.orbitControls.enableRotate = true;
        this.orbitControls.enablePan = false;
        this.orbitControls.minDistance = this._minDist;
        this.orbitControls.maxDistance = this._maxDist;

        this.orbitControls.addEventListener('change', (event) => {
            this.OrbitChange();
        });
    }


}
